import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Users, Clock, BarChart3, Smartphone } from 'lucide-react';

const Features = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <Users className="h-8 w-8 text-accent" />,
      title: "Daily Task Assignment & Management",
      description: "Efficiently assign and manage daily tasks. Employees update task status at day's end, making tracking streamlined and organized.",
      image: "https://images.unsplash.com/photo-1677078610588-aed2834ad968?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxwcm9qZWN0JTIwbWFuYWdlbWVudHxlbnwwfHx8fDE3NjA2Njg5Mzd8MA&ixlib=rb-4.1.0&q=85",
      features: ["Task assignment interface", "Progress tracking", "Team collaboration"]
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-accent" />,
      title: "Comprehensive Effort Reports",
      description: "Get clear breakdown of effort expended over the past 12 months with corresponding costs for each month and department.",
      image: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NjA2MjQyNjJ8MA&ixlib=rb-4.1.0&q=85",
      features: ["Monthly cost analysis", "Department insights", "Resource planning"]
    },
    {
      icon: <Clock className="h-8 w-8 text-accent" />,
      title: "Billable vs Non-Billable Tracking",
      description: "Track time spent on demonstrations, in-house development, and other non-billable activities with clear visibility.",
      image: "https://images.unsplash.com/photo-1686061594225-3e92c0cd51b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NjA2MjQyNjJ8MA&ixlib=rb-4.1.0&q=85",
      features: ["Time categorization", "Effort visibility", "Cost optimization"]
    },
    {
      icon: <Smartphone className="h-8 w-8 text-accent" />,
      title: "FREE Mobile App",
      description: "Complete mobile solution for attendance tracking, task management, and various other productivity features.",
      image: "https://images.unsplash.com/photo-1758770478125-4850521fd941?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxtb2JpbGUlMjBhcHAlMjBpbnRlcmZhY2V8ZW58MHx8fHwxNzYwNjY4OTcwfDA&ixlib=rb-4.1.0&q=85",
      features: ["Attendance marking", "Task viewing", "Time tracking"]
    }
  ];

  return (
    <section id="features" className="py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            Here's the solution to all your problems:
            <span className="text-gradient-logo block animate-gradient-shift bg-200%">
              EfforTrak
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Comprehensive project management and cost tracking solution designed for growing teams
          </p>
        </div>

        {/* Features Grid */}
        <div className="space-y-20">
          {features.map((feature, index) => (
            <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center animate-fade-in ${
              index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
            }`} style={{ animationDelay: `${index * 0.2}s` }}>
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="flex items-center space-x-3">
                  <div className="p-3 rounded-full bg-accent/10">
                    {feature.icon}
                  </div>
                  <span className="text-sm font-semibold text-accent uppercase tracking-wider">
                    Feature {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                
                <h3 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                  {feature.title}
                </h3>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
                
                <div className="space-y-3">
                  {feature.features.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-elegant hover-lift">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-80 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-accent/20"></div>
                  
                  {/* Floating badge */}
                  <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-card">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-muted-foreground">Live Demo</span>
                    </div>
                  </div>
                </div>
                
                {/* Background decoration */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-accent/10 rounded-full blur-xl"></div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/10 rounded-full blur-xl"></div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16 animate-fade-in">
          <Button 
            onClick={() => scrollToSection('contact')}
            size="lg" 
            className="bg-gradient-warm hover:shadow-warm transition-all duration-300 px-8 py-6 text-base text-white"
          >
            Start Your Free Trial
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Features;